import { SdkResponse, transformResponse, HttpClient } from '@descope/core-js-sdk';
import apiPaths from './paths';
import {
  CreateSSOApplicationResponse,
  SSOApplication,
  SSOApplicationSecretResponse,
  OidcApplicationOptions,
  SamlApplicationOptions,
  WsFedApplicationOptions,
} from './types';

type MultipleSSOApplicationResponse = {
  apps: SSOApplication[];
};

const withSSOApplication = (httpClient: HttpClient) => ({
  createOidcApplication: (
    options: OidcApplicationOptions,
  ): Promise<SdkResponse<CreateSSOApplicationResponse>> =>
    transformResponse(
      httpClient.post(apiPaths.ssoApplication.oidcCreate, {
        ...options,
        enabled: options.enabled ?? true,
      }),
    ),
  createSamlApplication: (
    options: SamlApplicationOptions,
  ): Promise<SdkResponse<CreateSSOApplicationResponse>> =>
    transformResponse(
      httpClient.post(apiPaths.ssoApplication.samlCreate, {
        ...options,
        enabled: options.enabled ?? true,
      }),
    ),
  updateOidcApplication: (
    options: OidcApplicationOptions & { id: string },
  ): Promise<SdkResponse<never>> =>
    transformResponse(httpClient.post(apiPaths.ssoApplication.oidcUpdate, { ...options })),
  updateSamlApplication: (
    options: SamlApplicationOptions & { id: string },
  ): Promise<SdkResponse<never>> =>
    transformResponse(httpClient.post(apiPaths.ssoApplication.samlUpdate, { ...options })),
  createWsFedApplication: (
    options: WsFedApplicationOptions,
  ): Promise<SdkResponse<CreateSSOApplicationResponse>> =>
    transformResponse(
      httpClient.post(apiPaths.ssoApplication.wsfedCreate, {
        ...options,
        enabled: options.enabled ?? true,
      }),
    ),
  updateWsFedApplication: (
    options: WsFedApplicationOptions & { id: string },
  ): Promise<SdkResponse<never>> =>
    transformResponse(httpClient.post(apiPaths.ssoApplication.wsfedUpdate, { ...options })),
  delete: (id: string): Promise<SdkResponse<never>> =>
    transformResponse(httpClient.post(apiPaths.ssoApplication.delete, { id })),
  load: (id: string): Promise<SdkResponse<SSOApplication>> =>
    transformResponse<SSOApplication, SSOApplication>(
      httpClient.get(apiPaths.ssoApplication.load, {
        queryParams: { id },
      }),
      (data) => data,
    ),
  loadAll: (): Promise<SdkResponse<SSOApplication[]>> =>
    transformResponse<MultipleSSOApplicationResponse, SSOApplication[]>(
      httpClient.get(apiPaths.ssoApplication.loadAll, {}),
      (data) => data.apps,
    ),
  getApplicationSecret: (id: string): Promise<SdkResponse<SSOApplicationSecretResponse>> =>
    transformResponse<SSOApplicationSecretResponse, SSOApplicationSecretResponse>(
      httpClient.get(apiPaths.ssoApplication.secret, {
        queryParams: { id },
      }),
      (data) => data,
    ),
  rotateApplicationSecret: (id: string): Promise<SdkResponse<SSOApplicationSecretResponse>> =>
    transformResponse<SSOApplicationSecretResponse, SSOApplicationSecretResponse>(
      httpClient.post(apiPaths.ssoApplication.rotate, { id }),
      (data) => data,
    ),
});

export default withSSOApplication;
